import { DotSeparator } from '@frameio/shared/components/DotSeparator';
import { useFormattedRelativeDate } from '@frameio/utils';
import { Box, Spinner, Text } from '@frameio/vapor';
import type { TransferBatch } from '../../../types/uploader';
import { getTransferBatchHasLocalData } from '../../../utils';
import { JobSize } from '../JobSize';
import { ScrollingJob } from '../ScrollingJob';
import TEXT from './generated/TEXT/CompletedJobStatus';

TEXT.DEFINE({
  /** Status label when download is complete */
  downloaded: 'Downloaded',
  /** Status label when upload is complete */
  uploaded: 'Uploaded',
  /** Status label when transcoding is in progress */
  transcoding: 'Preparing…',
  /** Aria label for loading spinner */
  loadingAriaLabel: 'Loading',
});

export const CompletedJobStatus = ({ job }: { job: TransferBatch }) => {
  const { completedAt, progress, batchType } = job;
  const hasLocalData = getTransferBatchHasLocalData(job);
  const hasDownloaded = batchType === 'DOWNLOAD';
  const hasUploaded = batchType === 'UPLOAD';
  const canShowCompletedAt =
    completedAt && hasLocalData && progress?.bytesSent === progress?.bytesTotal;
  const canShowJobSize = hasLocalData;

  return (
    <>
      <ScrollingJob>
        <Text color="tertiary" variant="secondary">
          {hasDownloaded && TEXT.downloaded}
          {hasUploaded && TEXT.uploaded}
        </Text>

        {canShowCompletedAt && (
          <>
            <DotSeparator />
            <CompletedAtSpan date={completedAt} />
          </>
        )}

        {canShowJobSize && (
          <>
            <DotSeparator />
            <JobSize bytesTotal={progress?.bytesTotal} />
          </>
        )}
      </ScrollingJob>
      {batchType === 'UPLOAD' && (
        <>
          <DotSeparator />
          <Box data-chromatic="ignore" paddingRight={4}>
            <Spinner
              aria-label={TEXT.loadingAriaLabel}
              delay={0}
              isLoading={true}
              size="xx-small"
            />
          </Box>
          <Text color="body" variant="secondary">
            {TEXT.transcoding}
          </Text>
        </>
      )}
    </>
  );
};

const CompletedAtSpan = ({ date }: { date: string }) => (
  <span className="completed-at">
    {useFormattedRelativeDate({
      date: new Date(date),
      truncate: true,
      showSuffix: true,
    })}
  </span>
);
